import { useCallback, useEffect, useRef, useState } from 'react';

import { listarTurmas } from '../../jogadores/api/jogadores';
import type { TurmaResumo } from '../../jogadores/api/jogadores';
import { RankingDaTurma } from '../../operacao/ui/RankingDaTurma';
import { Aviso } from '../../participantes/ui/Aviso';
import type { AvisoDaTela } from '../../participantes/ui/Aviso';
import { formatarPontos, obterClassificacao } from '../api/classificacao';
import type { Classificacao } from '../api/classificacao';
import { manterSeDisponivel, useTurmaEmUso } from '../../../shared/turmaEmUso';

const INTERVALO_MS = 15_000;

type Aba = 'grupos' | 'individual';

const ABAS: readonly { id: Aba; rotulo: string }[] = [
    { id: 'grupos', rotulo: 'Grupo' },
    { id: 'individual', rotulo: 'Individual' },
];

/** O selo da posição: ouro, prata e bronze nas três primeiras, número nas demais (como o tablet). */
function Posicao({ posicao }: { posicao: number }) {
    const podio = posicao <= 3 ? ` posicao-ranking-${posicao}` : '';
    return <span className={`posicao-ranking${podio}`} aria-label={`${posicao}º lugar`}>{posicao}</span>;
}

/**
 * A classificação da turma, de grupo e individual, como no tablet do jogador. Mostra com o ranking
 * travado também: o toggle só decide o que o jogador vê. A tela não calcula nada (`RN-RA-009`).
 */
export function ClassificacaoPainel({ csrfToken = '' }: { csrfToken?: string }) {
    const [turmas, setTurmas] = useState<TurmaResumo[]>([]);
    const [turmaId, setTurmaId] = useTurmaEmUso();
    const [classificacao, setClassificacao] = useState<Classificacao | null>(null);
    const [erro, setErro] = useState(false);
    const [aba, setAba] = useState<Aba>('grupos');
    const [aviso, setAviso] = useState<AvisoDaTela | null>(null);
    const fecharAviso = useCallback(() => setAviso(null), []);
    const turmaAtual = useRef<string | null>(null);

    const carregar = useCallback(async (alvo: string) => {
        try {
            const resposta = await obterClassificacao(alvo);
            if (turmaAtual.current !== alvo) return;
            setClassificacao(resposta);
            setErro(false);
        } catch {
            if (turmaAtual.current === alvo) setErro(true);
        }
    }, []);

    useEffect(() => {
        listarTurmas()
            .then((lista) => {
                // Encerrada continua na lista (para consultar), depois das ativas.
                const ordenadas = [...lista.filter((turma) => turma.ativa), ...lista.filter((turma) => !turma.ativa)];
                setTurmas(ordenadas);
                setTurmaId((atual) => manterSeDisponivel(atual, ordenadas.map((turma) => turma.id), ordenadas[0]?.id ?? null));
            })
            .catch(() => setErro(true));
    }, []);

    useEffect(() => {
        turmaAtual.current = turmaId;
        // Nada da turma anterior fica na tela enquanto a nova carrega.
        setClassificacao(null);
        if (!turmaId) return undefined;
        void carregar(turmaId);
        const relogio = window.setInterval(() => {
            if (document.visibilityState === 'visible') void carregar(turmaId);
        }, INTERVALO_MS);
        return () => window.clearInterval(relogio);
    }, [turmaId, carregar]);

    return (
        <div className="classificacao">
            <div className="barra-turma-operacao">
                <label htmlFor="turma-ranking">ID da Turma<span aria-hidden="true">:</span></label>
                <select id="turma-ranking" value={turmaId ?? ''} onChange={(e) => setTurmaId(e.target.value)}>
                    {turmas.map((turma) => (
                        <option key={turma.id} value={turma.id}>{turma.ativa ? turma.id : `${turma.id} (encerrada)`}</option>
                    ))}
                </select>
                {turmaId && <RankingDaTurma turmaId={turmaId} csrfToken={csrfToken} aoAvisar={setAviso} />}
            </div>

            <div className="abas-painel abas-classificacao" role="tablist" aria-label="Tipo de ranking">
                {ABAS.map(({ id, rotulo }) => (
                    <button
                        key={id}
                        id={`aba-classificacao-${id}`}
                        className="aba-painel"
                        type="button"
                        role="tab"
                        aria-selected={aba === id}
                        aria-controls="lista-classificacao"
                        tabIndex={aba === id ? 0 : -1}
                        onClick={() => setAba(id)}
                    >
                        {rotulo}
                    </button>
                ))}
            </div>

            <div id="lista-classificacao" role="tabpanel" aria-labelledby={`aba-classificacao-${aba}`}>
                {erro ? (
                    <p className="classificacao-vazia" role="alert">Não foi possível carregar o ranking. Tentando de novo em instantes.</p>
                ) : !classificacao ? (
                    <p className="classificacao-vazia">Carregando…</p>
                ) : aba === 'grupos' ? (
                    classificacao.grupos.length === 0 ? (
                        <p className="classificacao-vazia">Ainda não há grupos nesta turma.</p>
                    ) : (
                        <ol className="lista-classificacao" aria-label="Ranking de grupos">
                            {classificacao.grupos.map((grupo) => (
                                <li key={grupo.id} className="linha-classificacao">
                                    <Posicao posicao={grupo.posicao} />
                                    <span className="nome-classificacao">{grupo.nome}</span>
                                    <span className="pontos-classificacao">{formatarPontos(grupo.pontos)}</span>
                                </li>
                            ))}
                        </ol>
                    )
                ) : classificacao.individual.length === 0 ? (
                    <p className="classificacao-vazia">Ainda não há participantes nesta turma.</p>
                ) : (
                    <ol className="lista-classificacao" aria-label="Ranking individual">
                        {classificacao.individual.map((jogador) => (
                            <li key={jogador.id} className="linha-classificacao">
                                <Posicao posicao={jogador.posicao} />
                                <span className="nome-classificacao">
                                    {jogador.nome}
                                    <span className="grupo-classificacao">{jogador.grupo_nome}</span>
                                </span>
                                <span className="pontos-classificacao">{formatarPontos(jogador.pontos)}</span>
                            </li>
                        ))}
                    </ol>
                )}
            </div>

            {aviso && <Aviso aviso={aviso} aoFechar={fecharAviso} />}
        </div>
    );
}
